import { cn } from "@/components/utils";
import { cva, VariantProps } from "class-variance-authority";
import { ArrowLeft, Menu } from "lucide-react";
import React from "react";
import Button from "../Button";

const AppBarVariants = cva(
  "flex flex-1 items-center justify-between h-14 shadow-md px-3",
  {
    variants: {
      varient: {
        default: "bg-card [&>*]:text-forground [&_.subtitle]:text-subline",
        primary: "bg-primary [&>*]:text-white/90 [&_.subtitle]:text-white/60",
        secondary: "bg-secondary [&>*]:text-white [&_.subtitle]:text-white/75",
      },
      position: {
        fixed: "fixed top-0 left-0 right-0 z-50",
      },
    },
    defaultVariants: {
      varient: "default",
      position: "fixed",
    },
  }
);

export interface AppBarProps
  extends React.HTMLAttributes<HTMLHeadingElement>,
    VariantProps<typeof AppBarVariants> {}

/**
 * App bar container
 * @returns AppBar JSX
 */
const AppBar = React.forwardRef<HTMLHeadingElement, AppBarProps>(
  ({ className, varient, position, ...props }, ref) => (
    <header
      ref={ref}
      className={cn(AppBarVariants({ className, varient, position }))}
      {...props}
    />
  )
);

AppBar.displayName = "AppBar";

export interface ToolbarProps extends React.HTMLAttributes<HTMLElement> {
  onMenuClick?: React.MouseEventHandler<HTMLButtonElement>;
  showBackButton?: boolean;
  /** user should send back action */
  onBackClick?: React.MouseEventHandler<HTMLButtonElement>;
  /** Menu label string */
  pageTitle?: string;
  /** Selected menu boolean */
  subtitle?: string;
  reactNode?: React.ReactNode;
}

/**
 * App toolbar items like back button, title, subtitle or any react node
 * @returns Toolbar JSX
 */
const Toolbar = React.forwardRef<HTMLButtonElement, ToolbarProps>(
  (
    {
      className,
      onMenuClick,
      showBackButton,
      onBackClick,
      pageTitle,
      subtitle,
      reactNode,
      ...props
    },
    ref
  ) => {
    return (
      <section
        {...props}
        ref={ref}
        className={`flex h-full items-center justify-start ${className || ""}`}
      >
        {onMenuClick ? (
          <Button onClick={onMenuClick} variant={"ghost"} size={"icon"}>
            <Menu />
          </Button>
        ) : null}
        {showBackButton ? (
          <Button onClick={onBackClick} variant={"ghost"} size={"icon"}>
            <ArrowLeft />
          </Button>
        ) : null}
        {pageTitle || subtitle ? (
          <div className="ml-3">
            <h1>{pageTitle}</h1>
            <p className="subtitle">{subtitle}</p>
          </div>
        ) : null}
        {reactNode || null}
      </section>
    );
  }
);

Toolbar.displayName = "Toolbar";

export interface navbarProps extends React.HTMLAttributes<HTMLElement> {}
/**
 * App bar navigation/other action buttons
 * Send only buttons with out any wrapper
 * @returns Navbar JSX
 */
const NavBar = React.forwardRef<HTMLButtonElement, navbarProps>(
  ({ className, children, ...props }, ref) => {
    return (
      <nav
        {...props}
        ref={ref}
        className={`ml-auto flex h-full items-center space-x-2 ${className}`}
      >
        {children}
      </nav>
    );
  }
);

NavBar.displayName = "NavBar";

export { AppBar, NavBar, Toolbar };
